/**
 * src/slotBuilder.js
 *
 * Derives bookable time slots for one day from:
 *   - staff free/busy (POST /api/staff-availability, Graph getStaffAvailability,
 *     segments in the viewer's timezone), and
 *   - the service's own availability for that day (GET /api/service-availability:
 *     windows in viewer-local minutes + the service's slot interval).
 *
 * A slot is offered only when it lies inside a service window AND at least one
 * eligible staff member is free for the whole block including buffers. Inside
 * service-defined hours (explicit windows) the service can override staff
 * working hours — only real calendar events block — so e.g. an event service
 * open on a Sunday offers Sunday slots even though the staff member's week is
 * Mon–Fri. Whether it does is the admin setting serviceHoursOverride
 * (default "everyDay"; "never" matches the Microsoft Bookings page). Only
 * bookable slots are returned; an empty list means "no time slots available".
 *
 * Dependency-free so it runs under vitest's Node environment.
 */

const MIN_PER_DAY = 1440;

/** Calendar date of a local Date as YYYY-MM-DD (NOT toISOString, which is UTC). */
export function ymdLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Current wall clock in an IANA zone → { ymd, minutes }. */
export function nowInTimeZone(tz, nowMs = Date.now()) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hourCycle: "h23",
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    }).formatToParts(new Date(nowMs));
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return { ymd: `${get("year")}-${get("month")}-${get("day")}`, minutes: Number(get("hour")) * 60 + Number(get("minute")) };
  } catch {
    const d = new Date(nowMs);
    return { ymd: ymdLocal(d), minutes: d.getHours() * 60 + d.getMinutes() };
  }
}

/** "2026-10-12T07:30:00" clipped to `ymd` → minutes in [0, 1440]. */
function segmentMinutes(dateTime, ymd) {
  const [datePart, timePart = "00:00"] = String(dateTime || "").split("T");
  if (datePart < ymd) return 0;
  if (datePart > ymd) return MIN_PER_DAY;
  if (timePart.startsWith("23:59:59")) return MIN_PER_DAY; // server clips day ends to 23:59:59
  const [h, m] = timePart.split(":").map(Number);
  return h * 60 + m;
}

function merge(list) {
  const sorted = list.filter((w) => w.end > w.start).sort((a, b) => a.start - b.start);
  const out = [];
  for (const w of sorted) {
    const last = out[out.length - 1];
    if (last && w.start <= last.end) last.end = Math.max(last.end, w.end);
    else out.push({ ...w });
  }
  return out;
}

const FREE_STATUSES = new Set(["available", "slotsAvailable"]);

/** Gaps between merged intervals within [0, 1440]. */
function complement(merged) {
  const out = [];
  let cur = 0;
  for (const w of merged) {
    if (w.start > cur) out.push({ start: cur, end: w.start });
    cur = Math.max(cur, w.end);
  }
  if (cur < MIN_PER_DAY) out.push({ start: cur, end: MIN_PER_DAY });
  return out;
}

const pad = (n) => String(n).padStart(2, "0");

function slotFor(startMin, availableStaffIds) {
  const h = Math.floor(startMin / 60), m = startMin % 60;
  const hr12 = h % 12 === 0 ? 12 : h % 12;
  return {
    key: `${pad(h)}:${pad(m)}`,
    label: `${hr12}:${pad(m)} ${h >= 12 ? "PM" : "AM"}`,
    available: true,
    pastSlot: false,
    availableStaffIds,
    period: h < 12 ? "Morning" : "Afternoon",
  };
}

/**
 * @param {object}   args
 * @param {object}   args.availability     - /api/staff-availability response
 * @param {string}   args.ymd              - the viewer-local day being shown
 * @param {number}   args.durationMins
 * @param {number}  [args.preBufferMins=0]
 * @param {number}  [args.postBufferMins=0]
 * @param {string}  [args.selectedStaffId] - null/undefined = any available staff
 * @param {object}  [args.dayInfo]         - service-availability day { windows }; omitted ⇒ whole day
 * @param {number}  [args.intervalMins]    - service slot interval; default = duration
 * @param {number}  [args.nowMinutes=-1]   - viewer-local "now" when ymd is today, else -1
 * @param {string}  [args.serviceHoursOverride="everyDay"] - "everyDay" | "daysOffOnly" | "never"
 *                                           (settings.availability.serviceHoursOverride)
 */
export function buildSlots({
  availability, ymd, durationMins, preBufferMins = 0, postBufferMins = 0,
  selectedStaffId = null, dayInfo, intervalMins, nowMinutes = -1, serviceHoursOverride = "everyDay",
}) {
  const items = availability?.value ?? availability?.staffAvailabilityItems ?? [];
  const step = intervalMins > 0 ? intervalMins : durationMins;
  if (!items.length || !(durationMins > 0) || !(step > 0)) return [];

  // Two views of each staff member's day:
  //   free      — Graph "available" segments (working hours minus calendar);
  //               used where staff hours govern.
  //   unblocked — everything except real calendar events ("busy",
  //               "tentative", …). Graph reports time outside working hours
  //               as "outOfOffice"; inside service-defined (explicit) hours
  //               the service overrides working hours, so that counts as free.
  //               (A genuine out-of-office event comes back as "busy", so it
  //               still blocks — verified on the tenant.)
  const freeByStaff = {};
  const unblockedByStaff = {};
  for (const item of items) {
    if (selectedStaffId && item.staffId !== selectedStaffId) continue;
    const segs = (item.availabilityItems || []).map((i) => ({
      status: i.status,
      start: segmentMinutes(i.startDateTime?.dateTime, ymd),
      end:   segmentMinutes(i.endDateTime?.dateTime, ymd),
    }));
    freeByStaff[item.staffId] = merge(segs.filter((s) => FREE_STATUSES.has(s.status)));
    unblockedByStaff[item.staffId] = complement(merge(segs.filter((s) => !FREE_STATUSES.has(s.status) && s.status !== "outOfOffice")));
  }
  const staffIds = Object.keys(freeByStaff);
  const serviceWindows = dayInfo ? dayInfo.windows || [] : [{ start: 0, end: MIN_PER_DAY, explicit: false }];
  // Minimum lead time is a cutoff, not a new anchor — the grid stays on the
  // real hours (7:00, 7:45…) and early entries are simply skipped.
  const earliest = dayInfo?.earliestStartMin ?? 0;

  // Which view applies to a staff member inside an explicit window:
  //   everyDay    → always unblocked (service hours override working hours)
  //   daysOffOnly → unblocked only when they have no working time that day
  //   never       → always free (working hours apply, like Microsoft Bookings)
  const explicitView = {};
  for (const id of staffIds) {
    const override = serviceHoursOverride === "everyDay"
      || (serviceHoursOverride === "daysOffOnly" && freeByStaff[id].length === 0);
    explicitView[id] = override ? unblockedByStaff[id] : freeByStaff[id];
  }

  const slots = new Map();
  for (const w of serviceWindows) {
    const byStaff = w.explicit ? explicitView : freeByStaff;
    const anyFree = merge(staffIds.flatMap((id) => byStaff[id]));
    for (const block of anyFree) {
      // Grid anchors at the start of each stretch of free time inside the
      // service window (e.g. 7:00, 7:45, … when the day opens at 7:00).
      const anchor = Math.max(w.start, block.start + preBufferMins);
      for (let t = anchor; t + durationMins <= w.end && t < block.end; t += step) {
        if (slots.has(t) || t <= nowMinutes || t < earliest) continue;
        const free = staffIds.filter((id) => byStaff[id].some(
          (f) => f.start <= t - preBufferMins && f.end >= t + durationMins + postBufferMins
        ));
        if (free.length) slots.set(t, slotFor(t, free));
      }
    }
  }
  return [...slots.keys()].sort((a, b) => a - b).map((t) => slots.get(t));
}
